/**
 * ETL Metadata Configurator
 * ─────────────────────────
 * Enterprise-grade pipeline metadata authoring screen.
 * Wired to your existing service pattern:
 *   Fetchexecutequery()  → [headers, url]  (POST raw SQL)
 *   CreateData()         → [headers, url]  (POST insert payload)
 *   Fetchupdatequery()   → [headers, url]  (POST update payload)
 *
 * Drop this file into your features/pipeline-designer folder and
 * import the three services from your own paths.
 *
 * REFACTORED: NodeConfigPanel is now schema-driven.
 * The hardcoded switch/case renderTypeFields() has been removed.
 * Node forms are now defined in NODE_CONFIG_SCHEMA — add new
 * node types there without touching any JSX.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import Select from "react-select";
import axios from "axios";
import * as XLSX from "xlsx";

// ─── adjust these imports to match your project paths ───────────────────────
import { Fetchexecutequery } from "../../../services/process/BRS1/brsService1";
import { CreateData }        from "../../../services/process/process_data/process_data";
import { Fetchupdatequery }  from "../../../services/process/BRS1/brsService1";
import FileUploadRenderer, { inferDatatype } from "./FileUploadRenderer";
// ────────────────────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════
// CONSTANTS  (metadata-driven – move to DB later)
// ═══════════════════════════════════════════════════════

const NODE_TYPES = [
  { value: "source",      label: "Source",      color: "#0F6E56", bg: "#E1F5EE", icon: "⬛" },
  { value: "target",      label: "Target",      color: "#185FA5", bg: "#E6F1FB", icon: "⬜" },
  { value: "transform",   label: "Transform",   color: "#534AB7", bg: "#EEEDFE", icon: "◆" },
  { value: "filter",      label: "Filter",      color: "#854F0B", bg: "#FAEEDA", icon: "▽" },
  { value: "join",        label: "Join",        color: "#993C1D", bg: "#FAECE7", icon: "⋈" },
  { value: "aggregation", label: "Aggregation", color: "#993556", bg: "#FBEAF0", icon: "Σ" },
  { value: "lookup",      label: "Lookup",      color: "#3B6D11", bg: "#EAF3DE", icon: "⌕" },
  { value: "expression",  label: "Expression",  color: "#A32D2D", bg: "#FCEBEB", icon: "ƒ" },
  { value: "router",      label: "Router",      color: "#5F5E5A", bg: "#F1EFE8", icon: "⑀" },
  { value: "validator",   label: "Validator",   color: "#0C447C", bg: "#E6F1FB", icon: "✓" },
];

const SOURCE_TYPES = [
  { value: "excel",      label: "Excel (.xlsx)" },
  { value: "csv",        label: "CSV" },
  { value: "fixed",      label: "Fixed-width" },
  { value: "json",       label: "JSON" },
  { value: "xml",        label: "XML" },
  { value: "mysql",      label: "MySQL" },
  { value: "postgresql", label: "PostgreSQL" },
  { value: "rest_api",   label: "REST API" },
  { value: "s3",         label: "Amazon S3" },
  { value: "ftp",        label: "FTP / SFTP" },
];

const TARGET_WRITE_MODES = [
  { value: "append",  label: "Append" },
  { value: "replace", label: "Replace / Truncate" },
  { value: "merge",   label: "Merge" },
  { value: "upsert",  label: "Upsert" },
];

const TRANSFORM_REGISTRY = [
  { id: "filter",       name: "Filter",          category: "Data Quality",  icon: "▽", description: "Filter rows by condition" },
  { id: "sort",         name: "Sort",             category: "Data Quality",  icon: "↕", description: "Sort rows by columns" },
  { id: "aggregation",  name: "Aggregation",      category: "Analytics",     icon: "Σ", description: "GROUP BY aggregations" },
  { id: "lookup",       name: "Lookup",           category: "Enrichment",    icon: "⌕", description: "Join with reference table" },
  { id: "join",         name: "Join",             category: "Enrichment",    icon: "⋈", description: "Inner / left / right join" },
  { id: "derived_col",  name: "Derived Column",   category: "Expression",    icon: "ƒ", description: "Compute new columns" },
  { id: "expression",   name: "Expression",       category: "Expression",    icon: "fx", description: "Arbitrary expression" },
  { id: "router",       name: "Router",           category: "Flow Control",  icon: "⑀", description: "Conditional routing" },
  { id: "dedup",        name: "Deduplication",    category: "Data Quality",  icon: "⊟", description: "Remove duplicate rows" },
  { id: "validation",   name: "Validation",       category: "Data Quality",  icon: "✓", description: "Schema / value validation" },
  { id: "standardize",  name: "Standardization",  category: "Cleansing",     icon: "≡", description: "Normalize values" },
  { id: "date_conv",    name: "Date Conversion",  category: "Cleansing",     icon: "📅", description: "Parse / reformat dates" },
  { id: "null_handle",  name: "Null Handling",    category: "Cleansing",     icon: "∅", description: "Fill / drop / flag nulls" },
  { id: "pivot",        name: "Pivot / Unpivot",  category: "Analytics",     icon: "⊞", description: "Reshape wide ↔ long" },
  { id: "window",       name: "Window Function",  category: "Analytics",     icon: "▭", description: "OVER (PARTITION BY …)" },
];

const PIPELINE_STATUS = [
  { value: "draft",     label: "Draft",     color: "#854F0B", bg: "#FAEEDA" },
  { value: "published", label: "Published", color: "#3B6D11", bg: "#EAF3DE" },
  { value: "archived",  label: "Archived",  color: "#5F5E5A", bg: "#F1EFE8" },
];

const PARAM_SUGGESTIONS = ["${RUN_DATE}", "${ENV}", "${FILE_NAME}", "${BATCH_ID}", "${SOURCE_ID}", "${TENANT_ID}"];

const ROLES_PERMISSIONS = {
  Admin:    ["create","edit","publish","approve","execute","rollback"],
  Designer: ["create","edit"],
  Operator: ["execute"],
  Reviewer: ["approve"],
  Viewer:   [],
};

// ═══════════════════════════════════════════════════════
// CSS
// ═══════════════════════════════════════════════════════

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}

  .cfg-root{font-family:'DM Sans',sans-serif;background:#f7f7f8;min-height:100vh;color:#111;font-size:0.82rem;}

  /* ── layout ── */
  .cfg-layout{display:flex;height:100vh;overflow:hidden;}
  .cfg-sidebar{width:220px;flex-shrink:0;background:#fff;border-right:1.5px solid #e4e4e4;display:flex;flex-direction:column;overflow:hidden;}
  .cfg-main{flex:1;display:flex;flex-direction:column;overflow:hidden;}
  .cfg-topbar{height:50px;background:#fff;border-bottom:1.5px solid #e4e4e4;display:flex;align-items:center;padding:0 1.2rem;gap:0.8rem;flex-shrink:0;}
  .cfg-canvas-area{flex:1;overflow:auto;padding:1.2rem;background:#f7f7f8;}
  .cfg-rightpanel{width:320px;flex-shrink:0;background:#fff;border-left:1.5px solid #e4e4e4;display:flex;flex-direction:column;overflow:hidden;transition:width 0.2s;}
  .cfg-rightpanel.closed{width:0;border:none;}

  /* ── sidebar ── */
  .cfg-sidebar-logo{padding:0.85rem 1rem;border-bottom:1.5px solid #e4e4e4;display:flex;align-items:center;gap:0.5rem;}
  .cfg-sidebar-logo-dot{width:8px;height:8px;border-radius:50%;background:#111;}
  .cfg-sidebar-logo-text{font-size:0.78rem;font-weight:700;letter-spacing:-0.01em;}
  .cfg-nav-section{padding:0.5rem 0.75rem 0.2rem;font-size:0.58rem;font-weight:700;letter-spacing:0.10em;text-transform:uppercase;color:#aaa;}
  .cfg-nav-item{display:flex;align-items:center;gap:0.5rem;padding:0.38rem 1rem;font-size:0.78rem;cursor:pointer;color:#555;border-radius:0;transition:all 0.1s;border-left:2px solid transparent;}
  .cfg-nav-item:hover{background:#f5f5f5;color:#111;}
  .cfg-nav-item.active{background:#f0f0ff;color:#3C3489;border-left-color:#534AB7;font-weight:500;}
  .cfg-nav-icon{font-size:0.9rem;width:16px;text-align:center;}

  /* ── node palette ── */
  .cfg-palette{padding:0.6rem 0.75rem;border-top:1.5px solid #e4e4e4;margin-top:auto;}
  .cfg-palette-title{font-size:0.58rem;font-weight:700;letter-spacing:0.10em;text-transform:uppercase;color:#aaa;margin-bottom:0.4rem;}
  .cfg-node-chip{display:flex;align-items:center;gap:0.35rem;padding:0.28rem 0.55rem;border-radius:999px;border:1.5px solid;font-size:0.69rem;font-weight:600;cursor:grab;margin-bottom:0.22rem;transition:opacity 0.15s;}
  .cfg-node-chip:hover{opacity:0.75;}

  /* ── topbar ── */
  .cfg-pipeline-name{font-size:0.9rem;font-weight:600;min-width:160px;}
  .cfg-status-pill{padding:0.15rem 0.6rem;border-radius:999px;font-size:0.68rem;font-weight:700;}
  .cfg-topbar-spacer{flex:1;}
  .cfg-btn{font-family:'DM Sans',sans-serif;font-size:0.74rem;font-weight:600;padding:0.28rem 0.85rem;border:1.5px solid #ccc;border-radius:999px;cursor:pointer;background:#fff;color:#111;transition:all 0.15s;display:inline-flex;align-items:center;gap:0.3rem;height:28px;}
  .cfg-btn:hover{background:#f5f5f5;}
  .cfg-btn-primary{background:#111;color:#fff;border-color:#111;}
  .cfg-btn-primary:hover{background:#333;}
  .cfg-btn-success{background:#16a34a;color:#fff;border-color:#16a34a;}
  .cfg-btn-success:hover{background:#15803d;}
  .cfg-btn-danger{background:#dc2626;color:#fff;border-color:#dc2626;}
  .cfg-btn-sm{height:24px;padding:0.15rem 0.6rem;font-size:0.68rem;}
  .cfg-btn:disabled{opacity:0.35;cursor:not-allowed;}

  /* ── canvas & nodes ── */
  .cfg-canvas{min-height:560px;position:relative;background:#fafafa;border:1.5px solid #e0e0e0;border-radius:16px;overflow:hidden;}
  .cfg-canvas-grid{position:absolute;inset:0;background-image:radial-gradient(#d8d8d8 1px,transparent 1px);background-size:24px 24px;pointer-events:none;}
  .cfg-node{position:absolute;border-radius:12px;border:2px solid;padding:0.55rem 0.8rem;cursor:pointer;user-select:none;min-width:120px;transition:box-shadow 0.15s;}
  .cfg-node.selected{box-shadow:0 0 0 3px rgba(83,74,183,0.35);}
  .cfg-node-header{display:flex;align-items:center;gap:0.35rem;margin-bottom:0.2rem;}
  .cfg-node-icon{font-size:0.9rem;}
  .cfg-node-name{font-size:0.76rem;font-weight:700;}
  .cfg-node-type{font-size:0.6rem;font-weight:600;opacity:0.7;text-transform:uppercase;letter-spacing:0.06em;}
  .cfg-node-port{width:10px;height:10px;border-radius:50%;background:#555;border:2px solid #fff;cursor:crosshair;flex-shrink:0;}
  .cfg-node-ports{display:flex;justify-content:space-between;margin-top:0.4rem;}
  .cfg-edge{position:absolute;pointer-events:none;}

  /* ── cards ── */
  .cfg-card{background:#fff;border:1.5px solid #e4e4e4;border-radius:14px;padding:0.9rem 1rem;margin-bottom:0.7rem;}
  .cfg-card-title{font-size:0.6rem;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:#888;margin-bottom:0.6rem;}
  .cfg-section-title{font-size:0.6rem;font-weight:700;letter-spacing:0.10em;text-transform:uppercase;color:#bbb;margin:0.7rem 0 0.35rem;}

  /* ── form ── */
  .cfg-field{display:flex;flex-direction:column;gap:0.15rem;margin-bottom:0.55rem;}
  .cfg-label{font-size:0.6rem;font-weight:700;color:#555;letter-spacing:0.06em;text-transform:uppercase;}
  .cfg-input{font-family:'DM Sans',sans-serif;font-size:0.8rem;padding:0.28rem 0.65rem;border:1.5px solid #ccc;border-radius:999px;outline:none;background:#fff;height:28px;color:#111;width:100%;}
  .cfg-input:focus{border-color:#534AB7;box-shadow:0 0 0 2px rgba(83,74,183,0.12);}
  .cfg-textarea{font-family:'DM Sans',sans-serif;font-size:0.8rem;padding:0.4rem 0.65rem;border:1.5px solid #ccc;border-radius:10px;outline:none;background:#fff;color:#111;width:100%;resize:vertical;min-height:64px;}
  .cfg-textarea:focus{border-color:#534AB7;}
  .cfg-select-wrap .react-select__control{border:1.5px solid #ccc!important;border-radius:999px!important;min-height:28px!important;height:28px!important;box-shadow:none!important;font-size:0.78rem;}
  .cfg-select-wrap .react-select__control--is-focused{border-color:#534AB7!important;box-shadow:0 0 0 2px rgba(83,74,183,0.12)!important;}
  .cfg-select-wrap .react-select__menu{font-size:0.78rem;border-radius:12px;border:1.5px solid #d8d8d8;z-index:9999;}
  .cfg-select-wrap .react-select__value-container{padding:0 0.55rem;}
  .cfg-select-wrap .react-select__indicator-separator{display:none;}
  .cfg-select-wrap .react-select__single-value{font-size:0.78rem;color:#111;font-weight:500;}
  .cfg-select-wrap .react-select__placeholder{font-size:0.75rem;color:#aaa;}
  .cfg-select-wrap .react-select__option{font-size:0.78rem;padding:6px 10px;border-radius:6px;cursor:pointer;}
  .cfg-select-wrap .react-select__option--is-selected{background:#111;color:#fff;}
  .cfg-select-wrap .react-select__indicators{height:28px;}
  .cfg-select-wrap .react-select__input-container{margin:0;padding:0;}

  /* ── right panel ── */
  .cfg-panel-header{padding:0.7rem 1rem;border-bottom:1.5px solid #e4e4e4;display:flex;align-items:center;justify-content:space-between;flex-shrink:0;}
  .cfg-panel-title{font-size:0.82rem;font-weight:700;}
  .cfg-panel-body{flex:1;overflow-y:auto;padding:0.8rem 1rem;scrollbar-width:thin;scrollbar-color:#d0d0d0 transparent;}
  .cfg-panel-footer{padding:0.65rem 1rem;border-top:1px solid #f0f0f0;display:flex;gap:0.4rem;justify-content:flex-end;flex-shrink:0;}

  /* ── transform registry grid ── */
  .cfg-registry-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:0.5rem;}
  .cfg-registry-card{border:1.5px solid #e4e4e4;border-radius:10px;padding:0.6rem 0.7rem;cursor:pointer;transition:all 0.15s;background:#fff;}
  .cfg-registry-card:hover{border-color:#534AB7;background:#EEEDFE;}
  .cfg-registry-card.selected{border-color:#534AB7;background:#EEEDFE;}
  .cfg-registry-icon{font-size:1.2rem;margin-bottom:0.3rem;}
  .cfg-registry-name{font-size:0.75rem;font-weight:700;color:#111;margin-bottom:0.12rem;}
  .cfg-registry-cat{font-size:0.6rem;color:#888;font-weight:600;text-transform:uppercase;letter-spacing:0.06em;}
  .cfg-registry-desc{font-size:0.68rem;color:#666;margin-top:0.2rem;}

  /* ── version timeline ── */
  .cfg-version-row{display:flex;align-items:center;gap:0.6rem;padding:0.5rem 0;border-bottom:1px solid #f0f0f0;}
  .cfg-version-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
  .cfg-version-label{font-size:0.75rem;font-weight:600;flex:1;}
  .cfg-version-meta{font-size:0.68rem;color:#999;}
  .cfg-version-badge{font-size:0.58rem;font-weight:700;padding:0.1rem 0.42rem;border-radius:999px;}

  /* ── params table ── */
  .cfg-param-row{display:flex;align-items:center;gap:0.4rem;margin-bottom:0.35rem;}
  .cfg-param-key{font-family:'DM Mono',monospace;font-size:0.72rem;background:#f0f0f0;border-radius:6px;padding:0.12rem 0.45rem;color:#3C3489;font-weight:500;white-space:nowrap;}

  /* ── lineage canvas ── */
  .cfg-lineage-canvas{position:relative;height:220px;background:#fafafa;border:1.5px solid #e0e0e0;border-radius:12px;overflow:hidden;}
  .cfg-lineage-node{position:absolute;border-radius:8px;border:1.5px solid;padding:0.28rem 0.65rem;font-size:0.7rem;font-weight:600;white-space:nowrap;}

  /* ── tabs ── */
  .cfg-tabs{display:flex;border-bottom:1.5px solid #e4e4e4;padding:0 1.2rem;background:#fff;gap:0;}
  .cfg-tab{padding:0.6rem 1rem;font-size:0.78rem;cursor:pointer;color:#888;border-bottom:2px solid transparent;margin-bottom:-1.5px;font-weight:500;transition:all 0.15s;}
  .cfg-tab.active{color:#3C3489;border-bottom-color:#534AB7;font-weight:700;}
  .cfg-tab:hover:not(.active){color:#333;}

  /* ── badge ── */
  .cfg-badge{display:inline-flex;align-items:center;padding:0.1rem 0.42rem;border-radius:999px;font-size:0.6rem;font-weight:700;}

  /* ── table ── */
  .cfg-table-wrap{overflow-x:auto;border-radius:10px;border:1.5px solid #e4e4e4;}
  .cfg-table{width:100%;border-collapse:collapse;font-size:0.78rem;}
  .cfg-table th{background:#f5f5f5;font-weight:700;font-size:0.6rem;letter-spacing:0.06em;text-transform:uppercase;color:#555;padding:0.42rem 0.7rem;text-align:left;border-bottom:1.5px solid #e4e4e4;}
  .cfg-table td{padding:0.38rem 0.7rem;border-bottom:1px solid #f0f0f0;vertical-align:middle;}
  .cfg-table tr:last-child td{border-bottom:none;}
  .cfg-table tr:hover td{background:#fafafa;}

  /* ── modal ── */
  .cfg-overlay{position:absolute;inset:0;background:rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;z-index:1000;}
  .cfg-modal{background:#fff;border-radius:18px;width:500px;max-width:92%;max-height:80vh;display:flex;flex-direction:column;overflow:hidden;}
  .cfg-modal-header{padding:0.85rem 1.1rem;border-bottom:1.5px solid #f0f0f0;display:flex;justify-content:space-between;align-items:center;flex-shrink:0;}
  .cfg-modal-title{font-size:0.9rem;font-weight:700;}
  .cfg-modal-close{background:none;border:none;font-size:1rem;cursor:pointer;color:#aaa;}
  .cfg-modal-body{flex:1;overflow-y:auto;padding:0.9rem 1.1rem;}
  .cfg-modal-footer{padding:0.65rem 1.1rem;border-top:1px solid #f0f0f0;display:flex;gap:0.4rem;justify-content:flex-end;flex-shrink:0;}

  /* ── expression editor ── */
  .cfg-expr-editor{background:#1e1e2e;border-radius:10px;padding:0.65rem 0.85rem;font-family:'DM Mono',monospace;font-size:0.78rem;color:#cdd6f4;min-height:80px;white-space:pre-wrap;outline:none;border:1.5px solid #313244;}
  .cfg-expr-editor:focus{border-color:#534AB7;}
  .cfg-fn-grid{display:flex;flex-wrap:wrap;gap:0.3rem;margin-top:0.5rem;}
  .cfg-fn-chip{background:#f0f0ff;border:1px solid #AFA9EC;border-radius:6px;padding:0.12rem 0.42rem;font-size:0.68rem;font-weight:600;color:#3C3489;cursor:pointer;font-family:'DM Mono',monospace;}
  .cfg-fn-chip:hover{background:#EEEDFE;}

  /* ── connector card ── */
  .cfg-connector-card{border:1.5px solid #e4e4e4;border-radius:12px;padding:0.75rem;display:flex;align-items:center;gap:0.6rem;margin-bottom:0.5rem;cursor:pointer;transition:all 0.15s;}
  .cfg-connector-card:hover{border-color:#534AB7;}
  .cfg-connector-icon{width:36px;height:36px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:1rem;flex-shrink:0;}
  .cfg-connector-name{font-size:0.8rem;font-weight:700;}
  .cfg-connector-type{font-size:0.65rem;color:#888;}

  /* ── scrollbar ── */
  .cfg-panel-body::-webkit-scrollbar{width:5px;}
  .cfg-panel-body::-webkit-scrollbar-thumb{background:#d0d0d0;border-radius:3px;}
`;

// ═══════════════════════════════════════════════════════
// SMALL SHARED COMPONENTS
// ═══════════════════════════════════════════════════════

function CfgSelect({ options, value, onChange, placeholder, isDisabled }) {
  return (
    <Select
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder || "Select…"}
      classNamePrefix="react-select"
      className="cfg-select-wrap"
      isDisabled={isDisabled}
      menuPortalTarget={document.body}
      styles={{ menuPortal: b => ({ ...b, zIndex: 99999 }) }}
    />
  );
}

function Field({ label, children }) {
  return (
    <div className="cfg-field">
      {label && <label className="cfg-label">{label}</label>}
      {children}
    </div>
  );
}

function Badge({ label, color, bg }) {
  return (
    <span className="cfg-badge" style={{ background: bg, color }}>
      {label}
    </span>
  );
}

// ═══════════════════════════════════════════════════════
// NODE on CANVAS
// ═══════════════════════════════════════════════════════

function PipelineNode({ node, selected, onSelect, onDragStart }) {
  const nt = NODE_TYPES.find(t => t.value === node.type) || NODE_TYPES[0];
  return (
    <div
      className={`cfg-node${selected ? " selected" : ""}`}
      style={{
        left: node.x, top: node.y,
        background: nt.bg,
        borderColor: nt.color,
        color: nt.color,
        minWidth: 130,
      }}
      onClick={e => { e.stopPropagation(); onSelect(node.id); }}
      onMouseDown={e => onDragStart(e, node.id)}
    >
      <div className="cfg-node-header">
        <span className="cfg-node-icon">{nt.icon}</span>
        <div>
          <div className="cfg-node-name">{node.name}</div>
          <div className="cfg-node-type">{nt.label}</div>
        </div>
      </div>
      <div className="cfg-node-ports">
        <div className="cfg-node-port" style={{ background: nt.color }} title="Input port" />
        <div className="cfg-node-port" style={{ background: nt.color }} title="Output port" />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// CANVAS EDGES (SVG overlay)
// ═══════════════════════════════════════════════════════

function EdgesLayer({ edges, nodes }) {
  return (
    <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
      <defs>
        <marker id="arr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M2 1L8 5L2 9" fill="none" stroke="#888" strokeWidth="1.5" strokeLinecap="round" />
        </marker>
      </defs>
      {edges.map((e, i) => {
        const src = nodes.find(n => n.id === e.source);
        const tgt = nodes.find(n => n.id === e.target);
        if (!src || !tgt) return null;
        const x1 = src.x + 130, y1 = src.y + 36;
        const x2 = tgt.x,       y2 = tgt.y + 36;
        const mx = (x1 + x2) / 2;
        return (
          <path
            key={i}
            d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
            fill="none"
            stroke="#aaa"
            strokeWidth="1.5"
            markerEnd="url(#arr)"
          />
        );
      })}
    </svg>
  );
}

// ═══════════════════════════════════════════════════════
// EXPRESSION BUILDER MINI
// ═══════════════════════════════════════════════════════

const FN_GROUPS = {
  "String":  ["TRIM()", "UPPER()", "LOWER()", "SUBSTR()", "CONCAT()", "REPLACE()", "LENGTH()", "LPAD()"],
  "Date":    ["TO_DATE()", "DATE_DIFF()", "ADD_DAYS()", "FORMAT_DATE()", "NOW()", "EXTRACT()"],
  "Math":    ["ROUND()", "FLOOR()", "CEIL()", "ABS()", "MOD()", "POWER()"],
  "Null":    ["ISNULL()", "IFNULL()", "COALESCE()", "NULLIF()"],
  "Convert": ["CAST()", "TO_INT()", "TO_DECIMAL()", "TO_CHAR()"],
  "Logic":   ["IF()", "CASE WHEN", "IIF()", "DECODE()"],
};

function ExpressionBuilder({ value, onChange }) {
  const [activeGroup, setActiveGroup] = useState("String");
  const ref = useRef(null);

  const insertFn = (fn) => {
    const el = ref.current;
    if (!el) return;
    const cur = el.innerText;
    onChange(cur + fn);
    el.innerText = cur + fn;
  };

  return (
    <div>
      <div
        ref={ref}
        className="cfg-expr-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={e => onChange(e.currentTarget.innerText)}
      >
        {value}
      </div>
      <div style={{ marginTop: "0.5rem", display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
        {Object.keys(FN_GROUPS).map(g => (
          <button
            key={g}
            className="cfg-btn cfg-btn-sm"
            style={{ borderRadius: 6, fontFamily: "inherit", background: activeGroup === g ? "#EEEDFE" : "" }}
            onClick={() => setActiveGroup(g)}
          >
            {g}
          </button>
        ))}
      </div>
      <div className="cfg-fn-grid">
        {(FN_GROUPS[activeGroup] || []).map(fn => (
          <button key={fn} className="cfg-fn-chip" onClick={() => insertFn(fn)}>{fn}</button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// SOURCE NODE HELPERS
// ═══════════════════════════════════════════════

const DEFAULT_SOURCE_CONFIG = {
  sourceType: "excel",
  file: "",
  sheetName: "",
  headerRow: 1,
  skipRows: 0,
  delimiter: ",",
  encoding: "utf-8",
};

const EMPTY_SOURCE_METADATA = {
  uploadedFileName: "",
  sheets: [],
  previewRows: [],
  detectedSchema: [],
  uploadedAt: "",
};

function shouldShowField(field, sourceConfig = {}) {
  if (!field.visibleWhen) {
    return true;
  }

  return Object.entries(field.visibleWhen).every(([key, allowedValues]) => {
    const currentValue = sourceConfig[key];
    return Array.isArray(allowedValues)
      ? allowedValues.includes(currentValue)
      : currentValue === allowedValues;
  });
}

function normalizeSourceNode(node = {}) {
  return {
    ...node,
    type: node.type || "source",
    name: node.name || "",
    description: node.description || "",
    config: { ...DEFAULT_SOURCE_CONFIG, ...(node.config || {}) },
    metadata: {
      ...EMPTY_SOURCE_METADATA,
      ...(node.metadata || {}),
      sheets: Array.isArray(node.metadata?.sheets) ? node.metadata.sheets : [],
      previewRows: Array.isArray(node.metadata?.previewRows) ? node.metadata.previewRows : [],
      detectedSchema: Array.isArray(node.metadata?.detectedSchema) ? node.metadata.detectedSchema : [],
    },
    outputSchema: Array.isArray(node.outputSchema) ? node.outputSchema : [],
    properties: node.properties && typeof node.properties === "object" ? node.properties : {},
  };
}

function parseDelimitedLine(line = "", delimiter = ",") {
  const values = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];

    if (char === '"') {
      if (inQuotes && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === delimiter && !inQuotes) {
      values.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  values.push(current);
  return values;
}

function buildOutputSchema(rows = []) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const columns = Object.keys(rows[0] || {});
  return columns.map(columnName => ({
    name: columnName,
    datatype: inferDatatype(rows[0][columnName]),
  }));
}

async function parseSourceFile(file, sourceType = "excel", config = {}) {
  const normalizedConfig = { ...DEFAULT_SOURCE_CONFIG, ...config };
  const uploadedAt = new Date().toISOString();

  if (!file) {
    return {
      metadata: {
        ...EMPTY_SOURCE_METADATA,
        uploadedAt,
      },
      outputSchema: [],
    };
  }

  try {
    if (sourceType === "csv") {
      const text = await file.text();
      const lines = text.replace(/\r/g, "").split("\n").filter(Boolean);

      if (lines.length === 0) {
        return {
          metadata: {
            ...EMPTY_SOURCE_METADATA,
            uploadedFileName: file.name,
            uploadedAt,
          },
          outputSchema: [],
        };
      }

      const headerRowIndex = Math.max(0, Number(normalizedConfig.headerRow) - 1);
      const header = parseDelimitedLine(lines[headerRowIndex] || "", normalizedConfig.delimiter);
      const dataLines = lines.slice(headerRowIndex + 1 + Number(normalizedConfig.skipRows || 0));
      const previewRows = dataLines.slice(0, 20).map(line => {
        const values = parseDelimitedLine(line, normalizedConfig.delimiter);
        return header.reduce((row, column, index) => ({
          ...row,
          [column || `column_${index + 1}`]: values[index] ?? "",
        }), {});
      });

      const detectedSchema = buildOutputSchema(previewRows);

      return {
        metadata: {
          uploadedFileName: file.name,
          sheets: [],
          previewRows,
          detectedSchema,
          uploadedAt,
        },
        outputSchema: detectedSchema,
      };
    }

    const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
    const sheetName = normalizedConfig.sheetName || workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    if (!worksheet) {
      return {
        metadata: {
          ...EMPTY_SOURCE_METADATA,
          uploadedFileName: file.name,
          uploadedAt,
        },
        outputSchema: [],
      };
    }

    const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    const headerRowIndex = Math.max(0, Number(normalizedConfig.headerRow) - 1);
    const header = rows[headerRowIndex] || [];
    const previewRows = rows
      .slice(headerRowIndex + 1 + Number(normalizedConfig.skipRows || 0), headerRowIndex + 1 + Number(normalizedConfig.skipRows || 0) + 20)
      .map(values => header.reduce((row, column, index) => ({
        ...row,
        [column || `column_${index + 1}`]: values?.[index] ?? "",
      }), {}));

    const detectedSchema = buildOutputSchema(previewRows);

    return {
      metadata: {
        uploadedFileName: file.name,
        sheets: workbook.SheetNames,
        previewRows,
        detectedSchema,
        uploadedAt,
      },
      outputSchema: detectedSchema,
    };
  } catch (error) {
    console.error("Failed to parse source file", error);
    return {
      metadata: {
        ...EMPTY_SOURCE_METADATA,
        uploadedFileName: file?.name || "",
        uploadedAt,
      },
      outputSchema: [],
    };
  }
}

// ═══════════════════════════════════════════════════════
// NODE CONFIG SCHEMA
// ───────────────────────────────────────────────────────
// Add / modify node types here — no JSX changes required.
//
// Supported field types:
//   text              → <input type="text">
//   number            → <input type="number">
//   textarea          → <textarea>
//   select            → <CfgSelect> (react-select)
//   expression_editor → <ExpressionBuilder>
// ═══════════════════════════════════════════════════════

const NODE_CONFIG_SCHEMA = {
  source: {
    fields: [
      {
        name: "sourceType",
        label: "Source Type",
        type: "select",
        options: SOURCE_TYPES,
        placeholder: "Select source type…",
      },
      {
        name: "file",
        label: "Upload File",
        type: "file_upload",
        visibleWhen: {
          sourceType: ["excel", "csv"],
        },
      },
      {
        name: "sheetName",
        label: "Sheet Name",
        type: "select",
        visibleWhen: {
          sourceType: ["excel"],
        },
      },
      {
        name: "headerRow",
        label: "Header Row",
        type: "number",
        defaultValue: 1,
      },
      {
        name: "skipRows",
        label: "Skip Rows",
        type: "number",
        defaultValue: 0,
      },
      {
        name: "delimiter",
        label: "Delimiter",
        type: "text",
        visibleWhen: {
          sourceType: ["csv"],
        },
      },
      {
        name: "encoding",
        label: "Encoding",
        type: "select",
        options: [
          { value: "utf-8", label: "UTF-8" },
          { value: "utf-16", label: "UTF-16" },
          { value: "latin-1", label: "Latin-1" },
        ],
        placeholder: "Select encoding…",
      },
    ],
  },

  target: {
    fields: [
      {
        name: "targetType",
        label: "Target type",
        type: "select",
        options: [
          { value: "mysql",      label: "MySQL"      },
          { value: "postgresql", label: "PostgreSQL" },
          { value: "s3",         label: "S3"         },
          { value: "api",        label: "REST API"   },
          { value: "file",       label: "File"       },
        ],
        placeholder: "Select target type…",
      },
      {
        name: "table",
        label: "Table / path",
        type: "text",
        placeholder: "schema.table_name",
      },
      {
        name: "writeMode",
        label: "Write mode",
        type: "select",
        options: TARGET_WRITE_MODES,
        placeholder: "Select write mode…",
      },
      {
        name: "primaryKeys",
        label: "Primary key(s)",
        type: "text",
        placeholder: "id, batch_id",
      },
      {
        name: "batchSize",
        label: "Batch size",
        type: "number",
        placeholder: "1000",
      },
    ],
  },

  expression: {
    fields: [
      {
        name: "expression",
        label: "Expression",
        type: "expression_editor",
      },
      {
        name: "outputCol",
        label: "Output column name",
        type: "text",
        placeholder: "derived_amount",
      },
    ],
  },

  filter: {
    fields: [
      {
        name: "condition",
        label: "Filter condition",
        type: "textarea",
        placeholder: "amount > 0 AND status != 'CANCELLED'",
      },
      {
        name: "nullHandling",
        label: "Null handling",
        type: "select",
        options: [
          { value: "pass", label: "Pass nulls through" },
          { value: "drop", label: "Drop null rows"      },
          { value: "flag", label: "Flag as invalid"     },
        ],
        placeholder: "Select null handling…",
      },
    ],
  },

  join: {
    fields: [
      {
        name: "joinType",
        label: "Join type",
        type: "select",
        options: [
          { value: "inner", label: "Inner join"      },
          { value: "left",  label: "Left join"       },
          { value: "right", label: "Right join"      },
          { value: "full",  label: "Full outer join" },
        ],
        placeholder: "Select join type…",
      },
      {
        name: "joinOn",
        label: "Join condition",
        type: "textarea",
        placeholder: "left.order_id = right.order_id",
      },
    ],
  },

  transform: {
    fields: [
      {
        name: "rawConfig",
        label: "Configuration (JSON)",
        type: "textarea",
        placeholder: '{"param": "value"}',
      },
    ],
  },

  aggregation: {
    fields: [
      {
        name: "groupByColumns",
        label: "Group-by columns",
        type: "text",
        placeholder: "region, product_id",
      },
      {
        name: "aggregations",
        label: "Aggregations (JSON)",
        type: "textarea",
        placeholder: '[{"col":"amount","fn":"SUM","alias":"total_amount"}]',
      },
    ],
  },

  lookup: {
    fields: [
      {
        name: "lookupSource",
        label: "Lookup source",
        type: "text",
        placeholder: "reference.products",
      },
      {
        name: "lookupKey",
        label: "Lookup key",
        type: "text",
        placeholder: "product_id",
      },
      {
        name: "returnColumns",
        label: "Return columns",
        type: "text",
        placeholder: "product_name, category",
      },
    ],
  },

  router: {
    fields: [
      {
        name: "conditions",
        label: "Route conditions (JSON)",
        type: "textarea",
        placeholder: '[{"label":"High value","condition":"amount > 10000"}]',
      },
      {
        name: "defaultRoute",
        label: "Default route label",
        type: "text",
        placeholder: "default",
      },
    ],
  },

  validator: {
    fields: [
      {
        name: "rules",
        label: "Validation rules (JSON)",
        type: "textarea",
        placeholder: '[{"col":"email","rule":"NOT NULL"},{"col":"amount","rule":"amount > 0"}]',
      },
      {
        name: "onFail",
        label: "On validation failure",
        type: "select",
        options: [
          { value: "reject",      label: "Reject row"          },
          { value: "flag",        label: "Flag and continue"   },
          { value: "quarantine",  label: "Send to quarantine"  },
        ],
        placeholder: "Select failure behaviour…",
      },
    ],
  },
};

// Fallback schema for any node type not yet defined above
const GENERIC_SCHEMA = {
  fields: [
    {
      name: "rawConfig",
      label: "Configuration (JSON)",
      type: "textarea",
      placeholder: '{"param": "value"}',
    },
  ],
};

// ═══════════════════════════════════════════════════════
// DYNAMIC FIELD RENDERER
// ───────────────────────────────────────────────────────
// renderField(field, value, onChange)
//   field   — schema field descriptor object
//   value   — current value from node.properties[field.name]
//   onChange— (newValue) => void
// ═══════════════════════════════════════════════════════

function renderField(field, value, onChange) {
  switch (field.type) {
    case "text":
      return (
        <input
          className="cfg-input"
          value={value ?? ""}
          placeholder={field.placeholder || ""}
          onChange={e => onChange(e.target.value)}
        />
      );

    case "number":
      return (
        <input
          className="cfg-input"
          type="number"
          value={value ?? ""}
          placeholder={field.placeholder || ""}
          onChange={e => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        />
      );

    case "textarea":
      return (
        <textarea
          className="cfg-textarea"
          value={value ?? ""}
          placeholder={field.placeholder || ""}
          onChange={e => onChange(e.target.value)}
        />
      );

    case "select": {
      const selected = (field.options || []).find(o => o.value === value) || null;
      return (
        <CfgSelect
          options={field.options || []}
          value={selected}
          onChange={opt => onChange(opt?.value)}
          placeholder={field.placeholder}
        />
      );
    }

    case "expression_editor":
      return (
        <ExpressionBuilder
          value={value || ""}
          onChange={onChange}
        />
      );

    default:
      return (
        <input
          className="cfg-input"
          value={value || ""}
          placeholder={field.placeholder || ""}
          onChange={e => onChange(e.target.value)}
        />
      );
  }
}

// ═══════════════════════════════════════════════════════
// NODE CONFIG PANEL  (schema-driven — no switch/case)
// ═══════════════════════════════════════════════════════

function NodeConfigPanel({ node, onUpdate, onClose, onSourceUpload, onRefreshSourceSchema, sourceFileCache }) {
  const nt = NODE_TYPES.find(t => t.value === node.type) || NODE_TYPES[0];
  const normalizedNode = normalizeSourceNode(node);
  const [localNode, setLocalNode] = useState(normalizedNode);

  useEffect(() => {
    setLocalNode(normalizeSourceNode(node));
  }, [node]);

  const isSource = localNode.type === "source";
  const sourceConfig = localNode.config || DEFAULT_SOURCE_CONFIG;
  const sourceMetadata = localNode.metadata || EMPTY_SOURCE_METADATA;

  const upd = (k, v) => setLocalNode(p => ({ ...p, [k]: v }));
  const updProp = (k, v) => setLocalNode(p => ({ ...p, properties: { ...(p.properties || {}), [k]: v } }));
  const updConfig = (k, v) => setLocalNode(p => ({
    ...p,
    config: { ...(p.config || {}), [k]: v },
  }));

  const schema = NODE_CONFIG_SCHEMA[localNode.type] || GENERIC_SCHEMA;
  const visibleFields = schema.fields.filter(field => shouldShowField(field, sourceConfig));
  const sheetOptions = (sourceMetadata.sheets || []).map(sheetName => ({ value: sheetName, label: sheetName }));

  const sourcePreviewRows = Array.isArray(sourceMetadata.previewRows) ? sourceMetadata.previewRows : [];
  const sourcePreviewColumns = sourcePreviewRows.length > 0
    ? Object.keys(sourcePreviewRows[0])
    : [];

  const handleFieldChange = (field, value) => {
    if (isSource) {
      updConfig(field.name, value);
      if (field.name === "sheetName" && sourceFileCache?.[localNode.id]) {
        onSourceUpload?.(localNode.id, sourceFileCache[localNode.id], { sheetName: value });
      }
      return;
    }

    updProp(field.name, value);
  };

  return (
    <>
      <div className="cfg-panel-header">
        <div>
          <div className="cfg-panel-title">{nt.icon} {localNode.name}</div>
          <div style={{ fontSize: "0.65rem", color: "#888", marginTop: 2 }}>
            {nt.label} · node id: {localNode.id}
          </div>
        </div>
        <button className="cfg-modal-close" onClick={onClose}>✕</button>
      </div>

      <div className="cfg-panel-body">
        <Field label="Node name">
          <input
            className="cfg-input"
            value={localNode.name}
            onChange={e => upd("name", e.target.value)}
          />
        </Field>
        <Field label="Description">
          <textarea
            className="cfg-textarea"
            value={localNode.description || ""}
            onChange={e => upd("description", e.target.value)}
            placeholder="What does this node do?"
          />
        </Field>

        <div className="cfg-section-title">Node configuration</div>
        {visibleFields.map(field => {
          if (field.type === "file_upload") {
            return (
              <Field key={field.name} label={field.label}>
                <FileUploadRenderer
                  fileName={sourceConfig.file || sourceMetadata.uploadedFileName || ""}
                  onUpload={file => onSourceUpload?.(localNode.id, file)}
                />
              </Field>
            );
          }

          if (field.name === "sheetName") {
            return (
              <Field key={field.name} label={field.label}>
                <CfgSelect
                  options={sheetOptions}
                  value={sheetOptions.find(option => option.value === sourceConfig.sheetName) || null}
                  onChange={option => handleFieldChange(field, option?.value)}
                  placeholder={field.placeholder}
                />
              </Field>
            );
          }

          return (
            <Field key={field.name} label={field.label}>
              {renderField(
                field,
                isSource ? sourceConfig[field.name] : localNode.properties?.[field.name],
                value => handleFieldChange(field, value),
              )}
            </Field>
          );
        })}

        {isSource && (
          <>
            <div className="cfg-section-title">Preview</div>
            <div className="cfg-card" style={{ padding: "0.6rem", marginBottom: "0.8rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", alignItems: "center", marginBottom: "0.6rem" }}>
                <div style={{ fontSize: "0.72rem", color: "#555" }}>
                  {sourceMetadata.uploadedFileName || "No file uploaded yet"}
                </div>
                <button
                  className="cfg-btn cfg-btn-sm"
                  onClick={() => onRefreshSourceSchema?.(localNode.id, sourceConfig)}
                  disabled={!sourceFileCache?.[localNode.id]}
                >
                  Refresh Schema
                </button>
              </div>
              {sourcePreviewRows.length > 0 ? (
                <div className="cfg-table-wrap" style={{ maxHeight: 240 }}>
                  <table className="cfg-table">
                    <thead>
                      <tr>
                        {sourcePreviewColumns.map(column => (
                          <th key={column}>{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sourcePreviewRows.map((row, rowIndex) => (
                        <tr key={`${rowIndex}-${JSON.stringify(row)}`}>
                          {sourcePreviewColumns.map(column => (
                            <td key={`${rowIndex}-${column}`}>{String(row[column] ?? "")}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ color: "#888", fontSize: "0.75rem" }}>
                  Upload a CSV or Excel file to preview the first 20 rows.
                </div>
              )}
            </div>

            <div className="cfg-section-title">Detected schema</div>
            <div className="cfg-card" style={{ padding: "0.6rem" }}>
              {sourceMetadata.detectedSchema.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                  {sourceMetadata.detectedSchema.map((column, index) => (
                    <span
                      key={`${column.name}-${index}`}
                      className="cfg-badge"
                      style={{ background: "#EEEDFE", color: "#3C3489", border: "1px solid #AFA9EC" }}
                    >
                      {column.name}: {column.datatype}
                    </span>
                  ))}
                </div>
              ) : (
                <div style={{ color: "#888", fontSize: "0.75rem" }}>
                  Schema will be inferred after the file is uploaded.
                </div>
              )}
            </div>
          </>
        )}

        {!isSource && (
          <>
            <div className="cfg-section-title">Runtime parameters</div>
            {PARAM_SUGGESTIONS.map(p => (
              <span
                key={p}
                className="cfg-fn-chip"
                style={{ display: "inline-block", margin: "0 4px 4px 0", cursor: "pointer" }}
                onClick={() => {
                  const v = (localNode.properties?.rawConfig || "") + p;
                  updProp("rawConfig", v);
                }}
              >
                {p}
              </span>
            ))}
          </>
        )}
      </div>

      <div className="cfg-panel-footer">
        <button className="cfg-btn" onClick={onClose}>Discard</button>
        <button
          className="cfg-btn cfg-btn-success"
          onClick={() => { onUpdate(localNode); onClose(); }}
        >
          Apply
        </button>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN CONFIGURATOR
// ═══════════════════════════════════════════════════════

const INITIAL_NODES = [
  {
    id: "n1",
    type: "source",
    name: "Sales Excel",
    x: 40,
    y: 80,
    description: "",
    config: {
      sourceType: "excel",
      headerRow: 1,
      skipRows: 0,
      delimiter: ",",
      encoding: "utf-8",
    },
    metadata: {
      uploadedFileName: "",
      sheets: [],
      previewRows: [],
      detectedSchema: [],
      uploadedAt: "",
    },
    outputSchema: [],
  },
  { id: "n2", type: "transform", name: "Cleanse",      x: 230, y: 80,  description: "", properties: {} },
  { id: "n3", type: "filter",    name: "Active Only",  x: 420, y: 80,  description: "", properties: {} },
  { id: "n4", type: "expression",name: "Calc Amount",  x: 230, y: 210, description: "", properties: {} },
  { id: "n5", type: "target",    name: "DW Orders",    x: 610, y: 80,  description: "", properties: { writeMode: "upsert" } },
];

const INITIAL_EDGES = [
  { source: "n1", target: "n2" },
  { source: "n2", target: "n3" },
  { source: "n2", target: "n4" },
  { source: "n3", target: "n5" },
];

const VERSIONS = [
  { v: "v3", status: "published", by: "admin",     ts: "2025-06-12 10:30", current: true  },
  { v: "v2", status: "published", by: "designer1", ts: "2025-06-10 14:22", current: false },
  { v: "v1", status: "archived",  by: "designer1", ts: "2025-06-08 09:00", current: false },
];

const CONNECTORS = [
  { id: "mysql",    name: "MySQL",       type: "Database",     icon: "🗄", color: "#0F6E56", bg: "#E1F5EE", installed: true  },
  { id: "postgres", name: "PostgreSQL",  type: "Database",     icon: "🐘", color: "#185FA5", bg: "#E6F1FB", installed: true  },
  { id: "s3",       name: "Amazon S3",   type: "Cloud Storage",icon: "☁", color: "#854F0B", bg: "#FAEEDA", installed: true  },
  { id: "rest",     name: "REST API",    type: "API",          icon: "⚡", color: "#534AB7", bg: "#EEEDFE", installed: false },
  { id: "ftp",      name: "FTP / SFTP",  type: "File Transfer",icon: "📁", color: "#993C1D", bg: "#FAECE7", installed: false },
  { id: "excel",    name: "Excel / CSV", type: "File",         icon: "📊", color: "#3B6D11", bg: "#EAF3DE", installed: true  },
];

export default function ETLConfigurator() {
  // ── state ──────────────────────────────────────────
  const [activeTab, setActiveTab]       = useState("designer");
  const [nodes, setNodes]               = useState(INITIAL_NODES);
  const [edges, setEdges]               = useState(INITIAL_EDGES);
  const [selectedNodeId, setSelectedId] = useState(null);
  const [panelOpen, setPanelOpen]       = useState(false);
  const [pipelineName, setPipelineName] = useState("Sales ETL Pipeline");
  const [pipelineStatus, setPipelineStatus] = useState("draft");
  const [params, setParams]             = useState([
    { key: "${RUN_DATE}", value: "2025-06-12", scope: "pipeline" },
    { key: "${ENV}",      value: "staging",    scope: "pipeline" },
    { key: "${BATCH_ID}", value: "auto",        scope: "pipeline" },
  ]);
  const [saving, setSaving]             = useState(false);
  const [validating, setValidating]     = useState(false);
  const [validationResult, setValidResult] = useState(null);
  const [showPublishModal, setShowPublish] = useState(false);
  const [sourceFileCache, setSourceFileCache] = useState({});

  // ── drag ──────────────────────────────────────────
  const dragRef   = useRef(null);
  const canvasRef = useRef(null);

  const handleDragStart = (e, nodeId) => {
    dragRef.current = { nodeId, startX: e.clientX, startY: e.clientY };
    const node = nodes.find(n => n.id === nodeId);
    dragRef.current.origX = node.x;
    dragRef.current.origY = node.y;
  };

  useEffect(() => {
    const onMove = (e) => {
      // Snapshot the ref immediately — dragRef.current can become null
      // between this check and the async setNodes updater callback.
      const drag = dragRef.current;
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      setNodes(prev => prev.map(n =>
        n.id === drag.nodeId
          ? { ...n, x: Math.max(0, drag.origX + dx), y: Math.max(0, drag.origY + dy) }
          : n
      ));
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // ── add node from palette ──────────────────────────
  const addNode = (type) => {
    const id = `n${Date.now()}`;
    const nt = NODE_TYPES.find(t => t.value === type);

    const newNode = type === "source"
      ? {
          id,
          type,
          name: `New ${nt?.label || type}`,
          x: 60 + Math.random() * 200,
          y: 60 + Math.random() * 200,
          description: "",
          config: { ...DEFAULT_SOURCE_CONFIG },
          metadata: { ...EMPTY_SOURCE_METADATA },
          outputSchema: [],
        }
      : {
          id,
          type,
          name: `New ${nt?.label || type}`,
          x: 60 + Math.random() * 200,
          y: 60 + Math.random() * 200,
          description: "",
          properties: {},
        };

    setNodes(prev => [...prev, newNode]);
  };

  // ── delete node ──────────────────────────────────
  const deleteNode = (id) => {
    setNodes(prev => prev.filter(n => n.id !== id));
    setEdges(prev => prev.filter(e => e.source !== id && e.target !== id));
    if (selectedNodeId === id) { setSelectedId(null); setPanelOpen(false); }
  };

  // ── open node panel ──────────────────────────────
  const handleSelectNode = (id) => {
    setSelectedId(id);
    setPanelOpen(true);
  };

  const selectedNode = nodes.find(n => n.id === selectedNodeId);

  const updateNode = (updated) => {
    setNodes(prev => prev.map(n => n.id === updated.id ? updated : n));
  };

  const syncSourceNode = useCallback(async (nodeId, configPatch = {}, sourceFileOverride = null) => {
    const currentNode = nodes.find(node => node.id === nodeId);
    const sourceFile = sourceFileOverride || sourceFileCache[nodeId];

    if (!currentNode || !sourceFile) {
      return;
    }

    const normalized = normalizeSourceNode(currentNode);
    const nextConfig = { ...normalized.config, ...configPatch };
    const parsed = await parseSourceFile(sourceFile, nextConfig.sourceType, nextConfig);

    updateNode({
      ...currentNode,
      config: {
        ...nextConfig,
        file: sourceFile.name,
      },
      metadata: {
        ...normalized.metadata,
        ...parsed.metadata,
      },
      outputSchema: parsed.outputSchema,
    });
  }, [nodes, sourceFileCache]);

  const handleSourceUpload = useCallback(async (nodeId, file, configPatch = {}) => {
    if (!file) {
      return;
    }

    setSourceFileCache(prev => ({ ...prev, [nodeId]: file }));
    await syncSourceNode(nodeId, configPatch, file);
  }, [syncSourceNode]);

  const handleRefreshSourceSchema = useCallback(async (nodeId, configOverride = null) => {
    const currentNode = nodes.find(node => node.id === nodeId);
    if (!currentNode) {
      return;
    }

    const normalized = normalizeSourceNode(currentNode);
    await syncSourceNode(nodeId, configOverride || normalized.config);
  }, [nodes, syncSourceNode]);

  // ── SAVE to MySQL via CreateData ──────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        database: "etl_configurator",
        table: "pipelines",
        formData: {
          pipeline_name: pipelineName,
          status: pipelineStatus,
          nodes_json: JSON.stringify(nodes),
          edges_json: JSON.stringify(edges),
          params_json: JSON.stringify(params),
          updated_at: new Date().toISOString().slice(0,19).replace("T"," "),
        },
      };
      const creds = CreateData();
      await axios.post(creds[1], payload, creds[0]);
      alert("Pipeline configuration saved successfully.");
    } catch (err) {
      console.error("Save error", err);
      alert("Error saving pipeline configuration.");
    } finally {
      setSaving(false);
    }
  };

  // ── VALIDATE ─────────────────────────────────────
  const handleValidate = () => {
    setValidating(true);
    setTimeout(() => {
      const issues = [];
      nodes.forEach(n => {
        const inEdges  = edges.filter(e => e.target === n.id).length;
        const outEdges = edges.filter(e => e.source === n.id).length;
        if (n.type === "target" && inEdges === 0)  issues.push({ level: "error",   msg: `Target "${n.name}" has no incoming connection.` });
        if (n.type === "source" && outEdges === 0) issues.push({ level: "warning", msg: `Source "${n.name}" has no outgoing connection.` });
        if (!n.description)                        issues.push({ level: "info",    msg: `Node "${n.name}" has no description.` });
      });
      if (nodes.length === 0) issues.push({ level: "error", msg: "Pipeline has no nodes." });
      setValidResult(issues);
      setValidating(false);
    }, 600);
  };

  // ── PUBLISH ──────────────────────────────────────
  const handlePublish = async () => {
    try {
      const creds = Fetchupdatequery();
      const payload = {
        schema_name: "etl_configurator",
        table: "pipelines",
        set_column: { status: "published", published_at: new Date().toISOString().slice(0,19).replace("T"," ") },
        where: [{ pipeline_name: pipelineName }],
      };
      await axios.post(creds[1], payload, creds[0]);
      setPipelineStatus("published");
      setShowPublish(false);
      alert("Pipeline published.");
    } catch (err) {
      console.error("Publish error", err);
      alert("Error publishing pipeline.");
    }
  };

  // ── NAV SECTIONS ─────────────────────────────────
  const NAV = [
    { section: "Design", items: [
      { id: "designer",   icon: "⬡", label: "Pipeline designer" },
      { id: "registry",   icon: "◈", label: "Transform registry" },
      { id: "expression", icon: "ƒ", label: "Expression builder" },
    ]},
    { section: "Manage", items: [
      { id: "sources",    icon: "⬛", label: "Sources"    },
      { id: "targets",    icon: "⬜", label: "Targets"    },
      { id: "connectors", icon: "⚡", label: "Connectors" },
      { id: "templates",  icon: "▨", label: "Templates"  },
    ]},
    { section: "Govern", items: [
      { id: "versions",   icon: "⏱", label: "Versions"      },
      { id: "lineage",    icon: "⟶", label: "Lineage"       },
      { id: "params",     icon: "$",  label: "Parameters"    },
      { id: "rbac",       icon: "🔒", label: "Roles & access" },
    ]},
  ];

  // ════════════════════════════════════════════════
  // TAB CONTENT RENDERERS
  // ════════════════════════════════════════════════

  const renderDesigner = () => (
    <div style={{ position: "relative", minHeight: 560 }}>
      <div
        className="cfg-canvas"
        style={{ height: 560, cursor: "default" }}
        ref={canvasRef}
        onClick={() => { setSelectedId(null); setPanelOpen(false); }}
      >
        <div className="cfg-canvas-grid" />
        <EdgesLayer edges={edges} nodes={nodes} />
        {nodes.map(n => (
          <PipelineNode
            key={n.id}
            node={n}
            selected={n.id === selectedNodeId}
            onSelect={handleSelectNode}
            onDragStart={handleDragStart}
          />
        ))}
        {nodes.length === 0 && (
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", color:"#bbb", fontSize:"0.82rem" }}>
            Drag node types from the sidebar onto the canvas
          </div>
        )}
        {selectedNodeId && (
          <div style={{ position:"absolute", bottom:12, right:12, display:"flex", gap:6 }}>
            <button className="cfg-btn cfg-btn-sm" onClick={() => { setSelectedId(null); setPanelOpen(false); }}>Deselect</button>
            <button className="cfg-btn cfg-btn-sm cfg-btn-danger" onClick={() => deleteNode(selectedNodeId)}>Delete node</button>
          </div>
        )}
      </div>
      {validationResult && (
        <div className="cfg-card" style={{ marginTop: "0.7rem" }}>
          <div className="cfg-card-title">Validation results</div>
          {validationResult.length === 0
            ? <span style={{ color:"#16a34a", fontSize:"0.78rem", fontWeight:600 }}>✓ No issues found</span>
            : validationResult.map((r, i) => (
              <div key={i} style={{ display:"flex", gap:6, alignItems:"flex-start", marginBottom:4, fontSize:"0.78rem" }}>
                <span style={{ fontWeight:700, color: r.level==="error"?"#dc2626": r.level==="warning"?"#854F0B":"#185FA5", flexShrink:0, fontSize:"0.68rem", marginTop:2 }}>
                  {r.level==="error" ? "✕ ERROR" : r.level==="warning" ? "⚠ WARN" : "ℹ INFO"}
                </span>
                <span style={{ color:"#333" }}>{r.msg}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );

  const renderRegistry = () => (
    <div>
      <div style={{ marginBottom:"0.8rem", display:"flex", gap:"0.5rem", flexWrap:"wrap" }}>
        {["All","Data Quality","Analytics","Expression","Enrichment","Cleansing","Flow Control"].map(cat => (
          <button key={cat} className="cfg-btn cfg-btn-sm">{cat}</button>
        ))}
      </div>
      <div className="cfg-registry-grid">
        {TRANSFORM_REGISTRY.map(t => (
          <div key={t.id} className="cfg-registry-card" onClick={() => addNode("transform")}>
            <div className="cfg-registry-icon">{t.icon}</div>
            <div className="cfg-registry-name">{t.name}</div>
            <div className="cfg-registry-cat">{t.category}</div>
            <div className="cfg-registry-desc">{t.description}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderExpression = () => (
    <div>
      <div className="cfg-card">
        <div className="cfg-card-title">Expression editor</div>
        <ExpressionBuilder value="" onChange={() => {}} />
      </div>
      <div className="cfg-card">
        <div className="cfg-card-title">Test expression</div>
        <div style={{ display:"flex", gap:8 }}>
          <input className="cfg-input" placeholder="Sample input value" style={{ flex:1 }} />
          <button className="cfg-btn cfg-btn-primary">Run</button>
        </div>
        <div style={{ marginTop:"0.5rem", background:"#f5f5f5", borderRadius:8, padding:"0.5rem 0.75rem", fontSize:"0.78rem", color:"#888" }}>
          Output will appear here
        </div>
      </div>
    </div>
  );

  const renderSources = () => (
    <div className="cfg-card">
      <div className="cfg-card-title">Configured sources</div>
      <div className="cfg-table-wrap">
        <table className="cfg-table">
          <thead>
            <tr>
              <th>Name</th><th>Type</th><th>Connection</th><th>Schema / Path</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {[
              { name:"Sales Excel", type:"excel",   conn:"uploads/sales_q2.xlsx", schema:"Sheet1",         ok:true  },
              { name:"ERP GL Feed", type:"mysql",   conn:"${ENV}_erp_db",          schema:"gl.transactions",ok:true  },
              { name:"Vendor API",  type:"rest_api",conn:"https://api.vendor.com", schema:"/v1/invoices",   ok:false },
            ].map((s,i) => (
              <tr key={i}>
                <td style={{ fontWeight:600 }}>{s.name}</td>
                <td><Badge label={s.type} color="#185FA5" bg="#E6F1FB" /></td>
                <td style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.72rem", color:"#555" }}>{s.conn}</td>
                <td style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.72rem" }}>{s.schema}</td>
                <td>
                  <Badge label={s.ok?"Connected":"Error"} color={s.ok?"#3B6D11":"#A32D2D"} bg={s.ok?"#EAF3DE":"#FCEBEB"} />
                </td>
                <td><button className="cfg-btn cfg-btn-sm">Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop:"0.6rem" }}>
        <button className="cfg-btn cfg-btn-primary">+ Add source</button>
      </div>
    </div>
  );

  const renderTargets = () => (
    <div className="cfg-card">
      <div className="cfg-card-title">Configured targets</div>
      <div className="cfg-table-wrap">
        <table className="cfg-table">
          <thead>
            <tr><th>Name</th><th>Type</th><th>Table / Path</th><th>Write mode</th><th>Primary key</th><th></th></tr>
          </thead>
          <tbody>
            {[
              { name:"DW Orders",  type:"mysql", table:"dw.fact_orders",    mode:"upsert", pk:"order_id" },
              { name:"S3 Archive", type:"s3",    table:"s3://bucket/orders",mode:"append", pk:"-"        },
            ].map((t,i) => (
              <tr key={i}>
                <td style={{ fontWeight:600 }}>{t.name}</td>
                <td><Badge label={t.type} color="#0F6E56" bg="#E1F5EE" /></td>
                <td style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.72rem" }}>{t.table}</td>
                <td><Badge label={t.mode} color="#534AB7" bg="#EEEDFE" /></td>
                <td style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.72rem" }}>{t.pk}</td>
                <td><button className="cfg-btn cfg-btn-sm">Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop:"0.6rem" }}>
        <button className="cfg-btn cfg-btn-primary">+ Add target</button>
      </div>
    </div>
  );

  const renderConnectors = () => (
    <div>
      {CONNECTORS.map(c => (
        <div key={c.id} className="cfg-connector-card">
          <div className="cfg-connector-icon" style={{ background: c.bg, color: c.color }}>{c.icon}</div>
          <div style={{ flex:1 }}>
            <div className="cfg-connector-name">{c.name}</div>
            <div className="cfg-connector-type">{c.type}</div>
          </div>
          {c.installed
            ? <Badge label="Installed" color="#3B6D11" bg="#EAF3DE" />
            : <button className="cfg-btn cfg-btn-sm cfg-btn-primary">Install</button>
          }
        </div>
      ))}
    </div>
  );

  const renderTemplates = () => (
    <div>
      <div className="cfg-card-title" style={{ marginBottom:"0.6rem" }}>Pipeline templates</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:"0.6rem" }}>
        {[
          { name:"Bank Statement Parser", tags:["Finance","Excel"],    nodes:4 },
          { name:"Payroll Loader",        tags:["HR","MySQL"],         nodes:6 },
          { name:"ERP GL Loader",         tags:["Finance","API"],      nodes:5 },
          { name:"AR Aging Pipeline",     tags:["Finance","Report"],   nodes:7 },
          { name:"Inventory Sync",        tags:["Operations","MySQL"], nodes:3 },
        ].map((t,i) => (
          <div key={i} className="cfg-card" style={{ cursor:"pointer" }}>
            <div style={{ fontSize:"0.82rem", fontWeight:700, marginBottom:6 }}>{t.name}</div>
            <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:8 }}>
              {t.tags.map(tag => <Badge key={tag} label={tag} color="#534AB7" bg="#EEEDFE" />)}
            </div>
            <div style={{ fontSize:"0.7rem", color:"#888", marginBottom:8 }}>{t.nodes} nodes</div>
            <div style={{ display:"flex", gap:4 }}>
              <button className="cfg-btn cfg-btn-sm">Clone</button>
              <button className="cfg-btn cfg-btn-sm">Use</button>
            </div>
          </div>
        ))}
        <div className="cfg-card" style={{ cursor:"pointer", border:"1.5px dashed #ccc", display:"flex", alignItems:"center", justifyContent:"center", color:"#aaa", minHeight:100 }}>
          + Save current as template
        </div>
      </div>
    </div>
  );

  const renderVersions = () => (
    <div>
      <div className="cfg-card">
        <div className="cfg-card-title">Version history</div>
        {VERSIONS.map((v, i) => (
          <div key={i} className="cfg-version-row">
            <div className="cfg-version-dot"
              style={{ background: v.status==="published"?"#16a34a": v.status==="archived"?"#888":"#854F0B" }} />
            <div className="cfg-version-label">{v.v}</div>
            <Badge label={v.status}
              color={v.status==="published"?"#3B6D11":v.status==="archived"?"#5F5E5A":"#854F0B"}
              bg={v.status==="published"?"#EAF3DE":v.status==="archived"?"#F1EFE8":"#FAEEDA"} />
            {v.current && <Badge label="current" color="#185FA5" bg="#E6F1FB" />}
            <div className="cfg-version-meta">{v.by} · {v.ts}</div>
            <button className="cfg-btn cfg-btn-sm">Restore</button>
            <button className="cfg-btn cfg-btn-sm">Compare</button>
          </div>
        ))}
      </div>
      <div className="cfg-card">
        <div className="cfg-card-title">Approval workflow</div>
        {[
          { role:"Designer", action:"Submitted",      ts:"2025-06-12 09:10", done:true  },
          { role:"Reviewer", action:"Review pending",  ts:"—",               done:false },
          { role:"Admin",    action:"Publish pending", ts:"—",               done:false },
        ].map((s,i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"0.4rem 0", borderBottom:"1px solid #f0f0f0" }}>
            <div style={{ width:8, height:8, borderRadius:"50%", flexShrink:0, background: s.done?"#16a34a":"#ddd" }} />
            <span style={{ fontWeight:600, fontSize:"0.78rem", flex:1 }}>{s.role}</span>
            <span style={{ fontSize:"0.75rem", color:"#555" }}>{s.action}</span>
            <span style={{ fontSize:"0.68rem", color:"#aaa" }}>{s.ts}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderLineage = () => (
    <div className="cfg-card">
      <div className="cfg-card-title">Column-level lineage</div>
      <div className="cfg-lineage-canvas">
        <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none" }}>
          {[
            [80,40,220,40],[220,40,360,40],[360,40,500,40],
            [80,80,220,80],[220,80,360,80],
            [80,120,220,120],[220,120,360,120],[360,120,500,120],
          ].map(([x1,y1,x2,y2],i) => (
            <line key={i} x1={x1+60} y1={y1+10} x2={x2} y2={y2+10} stroke="#B5D4F4" strokeWidth="1.5" markerEnd="url(#larr)" />
          ))}
          <defs>
            <marker id="larr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M2 1L8 5L2 9" fill="none" stroke="#B5D4F4" strokeWidth="1.5" />
            </marker>
          </defs>
        </svg>
        {[
          { x:10,  y:20,  label:"order_id",       color:"#0F6E56", bg:"#E1F5EE" },
          { x:10,  y:60,  label:"amount",          color:"#0F6E56", bg:"#E1F5EE" },
          { x:10,  y:100, label:"order_date",      color:"#0F6E56", bg:"#E1F5EE" },
          { x:150, y:20,  label:"TRIM(order_id)",  color:"#534AB7", bg:"#EEEDFE" },
          { x:150, y:60,  label:"ROUND(amount)",   color:"#534AB7", bg:"#EEEDFE" },
          { x:150, y:100, label:"TO_DATE()",       color:"#534AB7", bg:"#EEEDFE" },
          { x:290, y:20,  label:"order_id",        color:"#854F0B", bg:"#FAEEDA" },
          { x:290, y:60,  label:"net_amount",      color:"#854F0B", bg:"#FAEEDA" },
          { x:290, y:100, label:"order_dt",        color:"#854F0B", bg:"#FAEEDA" },
          { x:430, y:20,  label:"dw_order_id",     color:"#185FA5", bg:"#E6F1FB" },
          { x:430, y:100, label:"dw_order_dt",     color:"#185FA5", bg:"#E6F1FB" },
        ].map((n,i) => (
          <div key={i} className="cfg-lineage-node"
            style={{ left:n.x, top:n.y, color:n.color, borderColor:n.color, background:n.bg, fontSize:"0.65rem" }}>
            {n.label}
          </div>
        ))}
        <div style={{ position:"absolute", bottom:8, left:8, display:"flex", gap:8, fontSize:"0.62rem", fontWeight:700 }}>
          {[["Source","#0F6E56","#E1F5EE"],["Transform","#534AB7","#EEEDFE"],["Filter","#854F0B","#FAEEDA"],["Target","#185FA5","#E6F1FB"]].map(([l,c,b])=>(
            <span key={l} style={{ color:c, background:b, padding:"0.1rem 0.4rem", borderRadius:4 }}>{l}</span>
          ))}
        </div>
      </div>
    </div>
  );

  const renderParams = () => (
    <div className="cfg-card">
      <div className="cfg-card-title">Runtime parameters</div>
      {params.map((p, i) => (
        <div key={i} className="cfg-param-row">
          <span className="cfg-param-key">{p.key}</span>
          <input className="cfg-input" value={p.value} style={{ flex:1 }}
            onChange={e => setParams(prev => prev.map((r,ri) => ri===i ? {...r,value:e.target.value}:r))} />
          <CfgSelect
            options={[{value:"pipeline",label:"Pipeline"},{value:"node",label:"Node"}]}
            value={{ value:p.scope, label:p.scope }}
            onChange={o => setParams(prev => prev.map((r,ri)=>ri===i?{...r,scope:o?.value}:r))} />
          <button className="cfg-btn cfg-btn-sm cfg-btn-danger"
            onClick={() => setParams(prev => prev.filter((_,ri) => ri !== i))}>✕</button>
        </div>
      ))}
      <button className="cfg-btn cfg-btn-sm" style={{ marginTop:6 }}
        onClick={() => setParams(prev => [...prev, { key:`\${PARAM_${prev.length+1}}`, value:"", scope:"pipeline" }])}>
        + Add parameter
      </button>
    </div>
  );

  const renderRBAC = () => (
    <div className="cfg-card">
      <div className="cfg-card-title">Roles and permissions</div>
      <div className="cfg-table-wrap">
        <table className="cfg-table">
          <thead>
            <tr>
              <th>Role</th>
              {["create","edit","publish","approve","execute","rollback"].map(p=><th key={p}>{p}</th>)}
            </tr>
          </thead>
          <tbody>
            {Object.entries(ROLES_PERMISSIONS).map(([role, perms]) => (
              <tr key={role}>
                <td style={{ fontWeight:700 }}>{role}</td>
                {["create","edit","publish","approve","execute","rollback"].map(p => (
                  <td key={p} style={{ textAlign:"center", fontSize:"0.9rem" }}>
                    {perms.includes(p)
                      ? <span style={{ color:"#16a34a" }}>✓</span>
                      : <span style={{ color:"#ddd" }}>–</span>
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const TAB_RENDER = {
    designer:   renderDesigner,
    registry:   renderRegistry,
    expression: renderExpression,
    sources:    renderSources,
    targets:    renderTargets,
    connectors: renderConnectors,
    templates:  renderTemplates,
    versions:   renderVersions,
    lineage:    renderLineage,
    params:     renderParams,
    rbac:       renderRBAC,
  };

  const statusMeta = PIPELINE_STATUS.find(s => s.value === pipelineStatus) || PIPELINE_STATUS[0];

  // ════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════

  return (
    <>
      <style>{CSS}</style>
      <div className="cfg-layout">

        {/* ── SIDEBAR ── */}
        <div className="cfg-sidebar">
          <div className="cfg-sidebar-logo">
            <div className="cfg-sidebar-logo-dot" />
            <span className="cfg-sidebar-logo-text">ETL Configurator</span>
          </div>

          {NAV.map(section => (
            <div key={section.section}>
              <div className="cfg-nav-section">{section.section}</div>
              {section.items.map(item => (
                <div
                  key={item.id}
                  className={`cfg-nav-item${activeTab === item.id ? " active" : ""}`}
                  onClick={() => { setActiveTab(item.id); setPanelOpen(false); }}
                >
                  <span className="cfg-nav-icon">{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>
          ))}

          {/* node palette – only shown on designer tab */}
          {activeTab === "designer" && (
            <div className="cfg-palette">
              <div className="cfg-palette-title">Node palette</div>
              {NODE_TYPES.map(nt => (
                <div
                  key={nt.value}
                  className="cfg-node-chip"
                  style={{ borderColor: nt.color, color: nt.color, background: nt.bg }}
                  onClick={() => addNode(nt.value)}
                  title={`Add ${nt.label} node`}
                >
                  <span>{nt.icon}</span>
                  <span>{nt.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── MAIN ── */}
        <div className="cfg-main">

          {/* topbar */}
          <div className="cfg-topbar">
            <input
              className="cfg-input"
              value={pipelineName}
              onChange={e => setPipelineName(e.target.value)}
              style={{ width: 220, borderRadius: 8 }}
            />
            <span className="cfg-status-pill"
              style={{ background: statusMeta.bg, color: statusMeta.color }}>
              {statusMeta.label}
            </span>
            <div className="cfg-topbar-spacer" />
            <button className="cfg-btn" onClick={handleValidate} disabled={validating}>
              {validating ? "Validating…" : "Validate"}
            </button>
            <button className="cfg-btn" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save draft"}
            </button>
            <button className="cfg-btn cfg-btn-success" onClick={() => setShowPublish(true)}>
              Publish
            </button>
          </div>

          {/* canvas area */}
          <div className="cfg-canvas-area" style={{ position:"relative" }}>
            {(TAB_RENDER[activeTab] || TAB_RENDER.designer)()}

            {/* publish modal */}
            {showPublishModal && (
              <div className="cfg-overlay">
                <div className="cfg-modal">
                  <div className="cfg-modal-header">
                    <span className="cfg-modal-title">Publish pipeline</span>
                    <button className="cfg-modal-close" onClick={() => setShowPublish(false)}>✕</button>
                  </div>
                  <div className="cfg-modal-body">
                    <p style={{ fontSize:"0.82rem", marginBottom:"0.8rem", color:"#333" }}>
                      Publishing will create a new immutable version and trigger the approval workflow.
                    </p>
                    <Field label="Release notes">
                      <textarea className="cfg-textarea" placeholder="What changed in this version?" />
                    </Field>
                    <Field label="Notify reviewers">
                      <input className="cfg-input" placeholder="reviewer@company.com" />
                    </Field>
                  </div>
                  <div className="cfg-modal-footer">
                    <button className="cfg-btn" onClick={() => setShowPublish(false)}>Cancel</button>
                    <button className="cfg-btn cfg-btn-success" onClick={handlePublish}>Publish now</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT PANEL (node config) ── */}
        <div className={`cfg-rightpanel${panelOpen && selectedNode ? "" : " closed"}`}>
          {panelOpen && selectedNode && (
            <NodeConfigPanel
              node={selectedNode}
              onUpdate={updateNode}
              onClose={() => { setPanelOpen(false); setSelectedId(null); }}
              onSourceUpload={handleSourceUpload}
              onRefreshSourceSchema={handleRefreshSourceSchema}
              sourceFileCache={sourceFileCache}
            />
          )}
        </div>

      </div>
    </>
  );
}